/**
 * Общие типы рабочего пространства (контракты §3,
 * docs/superpowers/specs/2026-10-21-redesign-production-contracts.md).
 *
 * Единственный источник ResourceRef и Page для новых междоменных модулей.
 * Форма на проводе закреплена фикстурой tests/fixtures/workspace-contract.json,
 * которую генерирует Java DTO (WorkspaceContractTest в qhub-analytics).
 */

export const RESOURCE_KINDS = [
	'service',
	'sql_database',
	's3_bucket',
	'kafka_instance',
	'nosql_database',
] as const;
export type ResourceKind = (typeof RESOURCE_KINDS)[number];

/** Ресурс определяется парой kind/id: SQL-база 42 и бакет 42 — разные объекты */
export type ResourceRef = { kind: ResourceKind; id: string };

export const OBSERVATION_STATES = [
	'known',
	'unknown',
	'not_collected',
	'unsupported',
	'stale',
] as const;
export type ObservationState = (typeof OBSERVATION_STATES)[number];

/** Регион, кластер и namespace независимы, каждый может быть неизвестен */
export type Placement = {
	region_id: string | null;
	cluster_id: string | null;
	namespace: string | null;
};

/**
 * Место типа данных в ресурсе (E3.6a): поле эндпоинта, колонка SQL, поле объекта S3, поле
 * схемы Kafka или NoSQL. Вид места определяет вид ресурса
 */
export const OCCURRENCE_LOCATION_KINDS = [
	'endpoint_field',
	'sql_column',
	's3_object_field',
	'kafka_schema_field',
	'nosql_schema_field',
] as const;
export type OccurrenceLocationKind = (typeof OCCURRENCE_LOCATION_KINDS)[number];

/**
 * Размещение типа данных: одно место — одна строка. direction есть только у наблюдения
 * трафика; evidence_refs — ссылки на образцы, пустые без права чтения образцов
 */
export type DataTypeOccurrence = {
	id: string;
	resource: ResourceRef;
	resource_name: string | null;
	location_kind: OccurrenceLocationKind;
	location_id: string;
	location_name: string | null;
	path: string | null;
	direction: string | null;
	last_observed_at: string | null;
	evidence_refs: string[];
};

/**
 * Использование типа данных (B8) по слою размещений: хранилища и сервисы, где он найден (сервис —
 * по правилу каталога, с местами за окно наблюдения), число мест и время их первого и последнего
 * наблюдения. Родитель включает места дочерних; без мест — нули и null: тип не используется
 */
export type DataTypeUsage = {
	data_type_id: string;
	storages: number;
	services: number;
	places: number;
	first_observed_at: string | null;
	last_observed_at: string | null;
};

export type Page<T> = {
	items: T[];
	total: number;
	filtered_total: number;
	limit: number;
	offset: number;
};

/** Смещение следующей страницы; undefined -- показаны все записи, подходящие под фильтры */
export function nextPageOffset(page: Page<unknown>): number | undefined {
	const next = page.offset + page.items.length;
	return page.items.length > 0 && next < page.filtered_total ? next : undefined;
}

/**
 * Счётчики каталога (E3.1): by_kind — ресурсы каждого вида при фильтрах запроса, кроме
 * фильтра видов; остальное — при всех фильтрах. high_risk — оценка риска выше 60.
 */
export type ResourceFacets = {
	by_kind: Record<ResourceKind, number>;
	filtered_total: number;
	with_sensitive_data: number;
	high_risk: number;
	/** Движки ресурсов вида (тип экземпляра) по тем же условиям, что by_kind */
	engines_by_kind: Record<ResourceKind, string[]>;
};

/** none — полный скан не нашёл чувствительных данных; неизвестный уровень — null */
export const SENSITIVITY_LEVELS = ['high', 'medium', 'low', 'none'] as const;
export type SensitivityLevel = (typeof SENSITIVITY_LEVELS)[number];

export type ResourceQuery = {
	q?: string;
	/** Только названные ресурсы (пары kind:id): карточки приоритетных событий одним запросом */
	refs?: ResourceRef[];
	kinds?: ResourceKind[];
	region_id?: string;
	cluster_id?: string;
	data_type_ids?: string[];
	risk_min?: number;
	sensitivity?: SensitivityLevel[];
	has_sensitive_data?: boolean;
	sort?: 'name' | 'risk' | 'last_observed_at';
	order?: 'asc' | 'desc';
	limit?: number;
	offset?: number;
};

export const RISK_COMPLETENESS = ['complete', 'partial', 'unknown'] as const;
export type RiskCompleteness = (typeof RISK_COMPLETENESS)[number];

export type RiskSummary = {
	model_id: string;
	score: number | null;
	completeness: RiskCompleteness;
	computed_at: string | null;
	factors: { code: string; contribution: number; evidence_refs: string[] }[];
};

/**
 * Карточка ресурса. null — «не собрано или неизвестно», не ноль;
 * data_type_ids: [] — сбор прошёл и типов не найдено.
 */
export type ResourceCard = {
	ref: ResourceRef;
	name: string;
	owner: string | null;
	placement: Placement;
	observation: ObservationState;
	last_observed_at: string | null;
	last_successful_scan_at: string | null;
	risk: RiskSummary | null;
	engine: string | null;
	instance: { id: string; name: string } | null;
	sensitivity: SensitivityLevel | null;
	data_type_ids: string[] | null;
	object_count: number | null;
	sensitive_field_count: number | null;
	size_bytes: number | null;
	endpoint_count: number | null;
};

export const WORKFLOW_STATUSES = ['new', 'in_progress', 'closed'] as const;
export type WorkflowStatus = (typeof WORKFLOW_STATUSES)[number];

/**
 * Разбор события (E2.4). До первого изменения — new без ответственного, версия 0;
 * assignee_name — снимок имени на момент назначения.
 */
export type EventWorkflow = {
	event_id: string;
	status: WorkflowStatus;
	assignee_id: string | null;
	assignee_name: string | null;
	version: number;
	updated_at: string | null;
};

/** Изменение разбора: версия, которую видел пользователь, и новое состояние */
export type WorkflowPatch = {
	expected_version: number;
	status: WorkflowStatus;
	assignee_id: string | null;
};

/** Запись истории разбора; actor_name — снимок имени автора, null — имя неизвестно */
export type WorkflowChange = {
	id: string;
	actor_id: string;
	actor_name: string | null;
	changed_at: string;
	before: EventWorkflow;
	after: EventWorkflow;
};

/** Интервал серии сводки: null — данных за интервал нет (до начала истории или за сроком журнала) */
export type WorkspaceSummaryBucket = {
	bucket_start: string;
	bucket_end: string;
	open_events: number | null;
	critical_events: number | null;
	/** Источники каталога на конец суток — из снимка инвентаря; null — снимка нет */
	sources_total: number | null;
	/** Чувствительные поля на конец суток — из снимка инвентаря; null — снимка нет */
	sensitive_fields: number | null;
};

export const SUMMARY_COMPLETENESS = ['complete', 'partial', 'unknown'] as const;
export type SummaryCompleteness = (typeof SUMMARY_COMPLETENESS)[number];

/**
 * Сводка обзора (E2.6, E3.8a): sources_total — ресурсы каталога всех видов; sensitive_fields —
 * места данных с персональными типами (null — ничего не собрано), по категориям — в
 * sensitive_fields_by_category; coverage.sensitive_fields — полнота этого счёта (partial —
 * число нижняя граница), coverage.history_from — начало достоверной серии
 */
export const SENSITIVE_GROUPS = ['pii', 'pci', 'secrets', 'custom', 'other'] as const;
export type SensitiveGroup = (typeof SENSITIVE_GROUPS)[number];
export type SensitiveGroups = Record<SensitiveGroup, number>;

export type WorkspaceSummary = {
	sources_total: number;
	open_events: number;
	critical_events: number;
	/** Открытые высокой важности за период */
	high_events: number;
	sensitive_fields: number | null;
	sensitive_fields_by_category: { pii: number; pci: number; secrets: number } | null;
	/**
	 * Те же места по группам продукта («Типы данных» аналитики): дети групп-типов ПДн, PCI,
	 * секретов и пользовательских типов, остальное — other; null — ничего не собрано
	 */
	sensitive_fields_by_group: SensitiveGroups | null;
	/** Сколько хранилищ и сервисов несут чувствительные поля; null — ничего не собрано */
	sensitive_sources: { storages: number; services: number } | null;
	coverage: { sensitive_fields: SummaryCompleteness; history_from: string | null };
	as_of: string;
	series: WorkspaceSummaryBucket[];
};

export const SERIES_OBSERVATIONS = ['complete', 'partial', 'not_collected'] as const;
export type SeriesObservation = (typeof SERIES_OBSERVATIONS)[number];

/**
 * Почасовой ряд запросов эндпоинта (E3.8a): оценка досчётом сэмплированного трафика по
 * UTC-часам [from, to); not_collected — наблюдения нет (value null), complete — час
 * подтверждён целиком (0 — настоящий ноль), partial — частично
 */
export type EndpointSeries = {
	unit: 'requests';
	aggregation: 'estimated';
	coverage_from: string | null;
	as_of: string;
	points: { from: string; to: string; value: number | null; observation: SeriesObservation }[];
};

/** Кандидат в ответственные: активный участник компании с правом разбора */
export type EventAssignee = { id: string; display_name: string };

export const PERMISSIONS = [
	'inventory.read',
	'inventory.write',
	'samples.read',
	'events.triage',
	'policies.write',
	'groups.write',
	'reports.create',
	'copilot.use',
	'training.use',
	'administration.write',
] as const;
export type Permission = (typeof PERMISSIONS)[number];

/* ── Действительные права ресурса (E3.4c) ── */

/** Решение по праву: запрет побеждает совпадающее разрешение, разрешение с условием — условное */
export const ACCESS_DECISIONS = ['allow', 'deny', 'conditional', 'unknown'] as const;
export type AccessDecision = (typeof ACCESS_DECISIONS)[number];

/** Действительное право: принципал, действия, цель, решение, условия, цепочка получения, свидетельства */
export interface EffectiveGrant {
	id: string;
	principal: string;
	actions: string[];
	target: string;
	decision: AccessDecision;
	/** Условия утверждения политики (ключ и значение); пусто — право без условий */
	conditions: string[];
	inherited_from: string[];
	evidence_refs: string[];
}

/** Поверхность политик снимка: collected, denied, failed, not_collected, unsupported */
export interface AccessCoverage {
	surface: string;
	state: string;
	reason: string | null;
}

/** Последняя попытка сбора: applied, pending, partial, failed */
export interface AccessAttempt {
	result: string;
	at: string | null;
	message: string | null;
}

/** Права ресурса: наблюдение, последний снимок, попытка, покрытие и страница прав */
export interface ResourceAccess {
	resource: ResourceRef;
	observation: ObservationState;
	last_successful_scan_at: string | null;
	last_attempt: AccessAttempt | null;
	coverage: AccessCoverage[];
	grants: Page<EffectiveGrant>;
}
